import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import EventBus from 'eventing-bus';

import AddAgentModal from '../pages/Agent/AddAgentModal';
import AddObjectModal from '../pages/Objects/AddObjectModal';
import { setNewDealModal } from '../store/Deals';
import { changePassword } from '../store/Auth';
import constants from '../constants';
import { getWorkspacePath } from '../utils/workspaceUtils';

const Modals = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { newDealModal } = useSelector(state => state['Deals']);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { userDetails } = useSelector(state => state['Settings']);
  const { requiresPasswordSetup } = useSelector(state => state['Auth']);

  // Password Setup Modal State
  const [passwordSetupModal, setPasswordSetupModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Show password setup modal immediately if requiresPasswordSetup flag is set
  // OR if user has temp_password field set
  useEffect(() => {
    // If password setup is NOT required, close the modal
    if (!requiresPasswordSetup) {
      setPasswordSetupModal(false);
      return;
    }

    // Show modal if flag is set OR temp_password exists
    if (requiresPasswordSetup || (userDetails && userDetails.temp_password && userDetails.temp_password !== '')) {
      // Show modal immediately - no delay
      setPasswordSetupModal(true);
      sessionStorage.setItem('passwordModalShown', 'true');
    }
  }, [requiresPasswordSetup, userDetails]);

  const handleDealSuccess = (newDeal) => {
    dispatch(setNewDealModal(false));
    if (newDeal && newDeal['_id']) {
      history.push(getWorkspacePath(`/deals/${newDeal['_id']}`, currentWorkspace?._id));
    }
  };

  // Password Setup Handlers
  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      EventBus.publish('error', 'Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      EventBus.publish('error', 'Password must be at least 6 characters long');
      return;
    }

    setIsFetching(true);
    dispatch(changePassword({
      formData: { newPassword: passwordForm.newPassword },
      successCallback: () => {
        setIsFetching(false);
        setPasswordSetupModal(false);
        sessionStorage.removeItem('passwordModalShown'); // Clear flag for future
        EventBus.publish('success', 'Password set successfully!');
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        // Clear password setup requirement flag
        dispatch({ type: 'SET_REQUIRES_PASSWORD_SETUP', payload: false });
        // Refresh user details to clear temp_password
        dispatch({ type: 'GET_ME' });
        // Notify that password setup is completed (for payment modal trigger)
        EventBus.publish('password:setup:completed');
      },
      failCallback: () => {
        setIsFetching(false);
      }
    }));
  };

  // SECURITY: No skipping allowed - user MUST set password
  // const handleSkipPassword = () => {
  //   setPasswordSetupModal(false);
  // };

  return (
    <>
      {newDealModal && (
        <AddObjectModal
          isOpen={newDealModal}
          objectType="deals"
          objectSchema={constants.objectsSchema['deal']}
          properties={null}
          onClose={() => dispatch(setNewDealModal(false))}
          onSuccess={handleDealSuccess}
        />
      )}
      <AddAgentModal />

      {/* Password Setup Modal */}
      <Modal
        isOpen={passwordSetupModal}
        className="main-modal new-user-modal modal-dialog-centered password-setup-modal"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader>
          <h3>Set Up Your Password</h3>
        </ModalHeader>
        <ModalBody>
          <div className="credits-area workspace-modal" style={{ paddingTop: '10px' }}>
            <div className="group-form" style={{ marginBottom: '20px' }}>
              <p className="modal-description" style={{ marginBottom: '0' }}>
                Welcome! Please set up your permanent password to secure your account.
                You'll use this password for future logins.
              </p>
            </div>

            <ValidatorForm className="validator-form" onSubmit={handlePasswordSubmit}>
              {/* New Password */}
              <div className="group-form" style={{ marginBottom: '15px' }}>
                <label>New Password</label>
                <div className="password-field-wrapper">
                  <TextValidator
                    required
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    name="newPassword"
                    validators={['required']}
                    className="login-input-fields"
                    placeholder="Enter your password"
                    onChange={handlePasswordChange}
                    value={passwordForm.newPassword}
                    errorMessages={['Password is required']}
                    type={showPassword.newPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    className="toggle-password-visibility"
                    onClick={() => togglePasswordVisibility('newPassword')}
                  >
                    <i className={`fa ${showPassword.newPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="group-form" style={{ marginBottom: '25px' }}>
                <label>Confirm Password</label>
                <div className="password-field-wrapper">
                  <TextValidator
                    required
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    name="confirmPassword"
                    validators={['required']}
                    className="login-input-fields"
                    placeholder="Confirm your password"
                    onChange={handlePasswordChange}
                    value={passwordForm.confirmPassword}
                    errorMessages={['Please confirm your password']}
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                  />
                  <button
                    type="button"
                    className="toggle-password-visibility"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                  >
                    <i className={`fa ${showPassword.confirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="footer-modal-area" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  className="btn-style-new"
                  disabled={isFetching}
                >
                  {isFetching ? 'Setting Password...' : 'Set Password'}
                </button>
              </div>
            </ValidatorForm>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default Modals;